import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import Auth from './auth';
import MenuContainer from './components/menu/menu_container';
import ProductManagementBase from './pages/inventory/v1/product';
import Attachments from './pages/files/attachments';
import AttachmentsV2 from './pages/files/attachments_v2';
import EventManagement from './pages/messages/events';
import EventManagementV2 from './pages/messages/v2/events_v2';
import AdvancedExport from './pages/files/export';
import AdvancedImport from './pages/files/import';
import AttachmentStorage from './pages/files/attachment_storage';
import LabelRedirect from './pages/files/label_redirect';
import OrderDetails from './pages/orders/order_details';
import OrderList from './pages/orders/order_list';
import OrderListV2 from './pages/orders/v2/order_list_v2';
import InvoiceDetailsBase from './pages/documents/invoice_details';
import AckDetailsBase from './pages/documents/ack_details';
import ReturnDetailsBase from './pages/documents/return_details';
import ShipmentDetailsBase from './pages/documents/shipment_details';
import ShipMultiple from './pages/orders/ship_multiple';
import InvoiceMultiple from './pages/orders/invoice_multiple';
import AckMultiple from './pages/orders/ack_multiple';
import Dashboard from './pages/dashboard/dashboard';
import MessageList from './pages/messages/message_list';
import Thread from './pages/messages/thread';
import Search from './pages/search/search';
import LogoManager from './pages/settings/logos';
import AccountInformation from './pages/settings/account_info';
import DocumentSettings from './pages/settings/document_settings';
import UserProfile from './pages/settings/user_profile';
import Notifications from './pages/settings/notifications';
import Monitoring from './pages/settings/monitoring';
import ApiAuthentication from './pages/settings/api';
import ShipmentOptions from './pages/settings/shipment_options';
import UserManagement from './pages/settings/users';
import CustomLookups from './pages/settings/custom_lookups';
import TestingOverview from './pages/testing/overview';
import TestCases from './pages/testing/cases';
import Scorecards from './pages/reports/scorecards';
import Onboarding from './pages/ccn/onboarding';
import OnboardingV2 from './pages/ccn/onboarding_v2';
import OnboardingDetails from './pages/ccn/onboarding_details';
import OnboardingDetailsV2Base from './pages/ccn/onboarding_details_v2';
import OnboardingSettings from './pages/ccn/onboarding_settings';
import OnboardingSettingsV2 from './pages/ccn/onboarding_settings_v2';
import AdvancedReports, { hasReport } from './pages/reports/powerbi';
import { resetThoughtSpotAuth } from './pages/reports/thoughtspot_auth_reset';
import { shouldRefreshThoughtSpotToken } from './pages/reports/thoughtspot_token';
import AskData from './pages/reports/ask_data';
import { oauthRoutes } from './pages/oauth/routes';
import Connections from './pages/settings/connections';
import Sourcing from './pages/settings/sourcing';
import DefaultAddresses from './pages/settings/default_addresses';
import DefaultPaymentTerms from './pages/settings/payment_terms';
import SubscriptionDetail from './pages/billing/subscription';
import SubscriptionInvoiceDetail from './pages/billing/subscription_invoice';
import BillingDashboard from './pages/billing/billing';
import NetworkCompanies from './pages/ccn/companies';
import CompanyProfile from './pages/ccn/profile';
import ProductProfile from './pages/settings/product_profile';
import EdiSettings from './pages/settings/edi';
import EdiReport from './pages/settings/edi_report';
import CatalogBase from './pages/inventory/v1/catalog';
import CatalogAttributesBase from './pages/inventory/v1/catalog_attributes';
import CatalogExportBase from './pages/inventory/v1/catalog_export';
import CatalogImportBase from './pages/inventory/v1/catalog_import';
import CatalogItemBase from './pages/inventory/v1/catalog_item';
import Categories from './pages/inventory/v1/categories';
import CatalogWizard from './pages/inventory/v1/catalog_wizard';
import ProductRules from './pages/inventory/v1/product_rules';
import ProductSpec from './pages/inventory/v1/product_feed_spec';

import InventoryManagementCenter from './pages/inventory/v2/inventory_management_center';
import InventorySettings from './pages/inventory/v2/inventory_management_settings';
import InventoryExport from './pages/inventory/v2/inventory_management_export';
import InventoryImport from './pages/inventory/v2/inventory_management_import';

import CatalogManagerCatalog from './pages/catalogmanager/containers/catalog/catalog';
import CatalogManagerProductDetails from './pages/catalogmanager/containers/product/product_details';
import CatalogManagerSettings from "./pages/catalogmanager/containers/settings/settings";
import CatalogManagerSettingsTaxonomy from "./pages/catalogmanager/containers/settings/containers/taxonomy";
import CatalogManagerSettingsGeneral from "./pages/catalogmanager/containers/settings/containers/general";
import CatalogManagerSettingsAutoApprove from "./pages/catalogmanager/containers/settings/containers/auto_approve";
import CatalogManagerSettingsAutoIgnore from "./pages/catalogmanager/containers/settings/containers/auto_ignore";
import CatalogManagerSettingsConnector from "./pages/catalogmanager/containers/settings/containers/connector";
import CatalogManagerSettingsBulkActions from "./pages/catalogmanager/containers/settings/containers/bulk_actions";
import CatalogManagerSettingsChannelSettings from "./pages/catalogmanager/containers/settings/containers/channel_settings";
import CatalogManagerAdvancedExport from "./pages/catalogmanager/containers/export/advanced_export";
import CatalogManagerAdvancedImport from "./pages/catalogmanager/containers/import/settings/advanced_import";
import CatalogManagerProductAdvancedImport from "./pages/catalogmanager/containers/import/product/advanced_supplier_import";
import DocumentFailures from './pages/messages/failures';
import FailedDocumentList from './pages/messages/failure_list';
import { AccountView as PaymentAccountView } from './pages/settings/payment';
import PaymentDashboard from './pages/payments/dashboard';
import PaymentTransfers from './pages/payments/transfers';
import TransferDetails from './pages/payments/transfer_details';
import NotFound from './components/not_found';
import ErrorBoundary from './components/error_boundary';
import { supplierOnly, retailerOnly } from './components/supplier_retailer_only';
import billableOnly, { billingEnabledOnly } from './components/billable_only';
import withCatalogEnabled from './pages/inventory/v1/with_catalog_enabled';
import withPOCEnabled from './utils/with_poc_enabled';
import { Route, BrowserRouter, Redirect, Switch } from "react-router-dom";
import axios from 'axios';
import { ThemeProvider } from '@mui/material/styles';
import Config from './config';
import { Dialog, DialogContent, DialogContentText, DialogActions, Button, CircularProgress, Card, CardContent, Toolbar, IconButton, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExtendedSelectBase from './components/select';
import Agreements from './pages/settings/agreements';
import ScheduledTasks from './pages/settings/scheduled_tasks';
import ScheduledTasksV2 from './pages/settings/v2/scheduled_tasks';
import { AckProperties, ReturnProperties, ScoreCardProperties, CatalogImportExportProperties, ShipmentProperties } from './pages/settings/default_properties';
import DocumentStandardsBase from './pages/documents/standards';
import Helpers, { clearCache as clearHelperCache, StorageHelper } from './helpers';
import logo from './logo.svg';
import { hubConnection as HubConnection } from 'signalr-no-jquery';
import theme from './theme';
import PartnerManagementCenter from "./pages/partnermanager/partner_management_center";
import PartnerManager from "./pages/partnermanager/partner_manager";
import PartnerManagementSettings from "./pages/partnermanager/partner_management_settings";
import SupplierGroupsSettings from "./pages/partnermanager/supplier_groups_settings";
import AgenticCommerceOverview from "./pages/agentic_commerce/overview";
import PayPalDashboard from "./pages/agentic_commerce/paypal_dashboard";
import StripeDashboard from "./pages/agentic_commerce/stripe_dashboard";
import { isThoughtSpotLiveboardEnabled, isThoughtSpotSpotterEnabled } from './pages/reports/thoughtspot_settings';

// select.js's forwardRef has no explicit generic, so TS infers only RefAttributes<unknown> as valid props.
const ExtendedSelect: any = ExtendedSelectBase;
// These are withRouter(...)-wrapped class components; withRouter's TS typing
// narrows the public props to RouteComponentProps and drops the extra
// app-shell props (setTitle, isBillable, signalr, isRetailer) passed at the
// call sites below. Behavior unchanged.
const InvoiceDetails: React.ComponentType<any> = InvoiceDetailsBase;
const AckDetails: React.ComponentType<any> = AckDetailsBase;
const ReturnDetails: React.ComponentType<any> = ReturnDetailsBase;
const ShipmentDetails: React.ComponentType<any> = ShipmentDetailsBase;
const DocumentStandards: React.ComponentType<any> = DocumentStandardsBase;
// Same HOC-generic-inference gap as withPOCEnabled below: these wrap a
// class/forwardRef component typed `any`, so `withXEnabled<P>` can't infer
// P and falls back to its bare constraint, dropping setTitle/isRetailer/
// isBillable/signalr passed at the route call sites.
const Catalog: React.ComponentType<any> = CatalogBase;
const CatalogAttributes: React.ComponentType<any> = CatalogAttributesBase;
const CatalogExport: React.ComponentType<any> = CatalogExportBase;
const CatalogImport: React.ComponentType<any> = CatalogImportBase;
const CatalogItem: React.ComponentType<any> = CatalogItemBase;
const ProductManagement: React.ComponentType<any> = ProductManagementBase;
// onboarding_details_v2.js is still plain JS (separate ticket's scope, not yet
// merged into this branch); it's a bare arrow function destructuring only
// `{ setTitle }`, so allowJs infers that narrow shape directly (unlike a
// class component or an untyped HOC wrapper, which infer to `any`). Relax it
// here since isRetailer is passed at the call site below.
const OnboardingDetailsV2: React.ComponentType<any> = OnboardingDetailsV2Base;

const USE_POC_ORDER_STYLES = true;
const USE_POC_ATTACHMENT_STYLES = true;
const USE_POC_EVENT_STYLES = true;
const USE_POC_SCHEDULED_TASKS_STYLES = true;

const Refresh = ({ path = '/' }) => (
    <Route
        path={path}
        component={({ history, location, match }) => {
            history.replace({
                ...location,
                pathname: location.pathname.substring(match.path.length)
            });
            return null;
        }}
    />
);

const SupplierAddresses = supplierOnly(DefaultAddresses);
const SupplierPaymentTerms = supplierOnly(DefaultPaymentTerms);
const SupplierAckProperties = supplierOnly(AckProperties);
const SupplierReturnProperties = supplierOnly(ReturnProperties);
const BillableCustomLookups = billableOnly(CustomLookups);
const ProductCatalogProperties: React.ComponentType<any> = withCatalogEnabled(CatalogImportExportProperties);
const SupplierShipmentProperties = supplierOnly(ShipmentProperties);
const RetailerProductRules = retailerOnly(ProductRules);
const BillableRetailerOnboarding = billableOnly(retailerOnly(Onboarding));
const BillableRetailerOnboardingV2 = billableOnly(retailerOnly(OnboardingV2));
const BillableRetailerOnboardingSettings = billableOnly(retailerOnly(OnboardingSettings));
const BillableRetailerOnboardingSettingsV2 = billableOnly(retailerOnly(OnboardingSettingsV2));
const WrappedSubscriptionDetail = billingEnabledOnly(SubscriptionDetail);
const WrappedSubscriptionInvoiceDetail = billingEnabledOnly(SubscriptionInvoiceDetail);
const WrappedBillingDashboard = billingEnabledOnly(BillingDashboard);
// withPOCEnabled<P extends {pocEnabled?: boolean}> can't infer P from an
// `any`-typed argument and falls back to the bare constraint, dropping the
// extra app-shell props (setTitle, retailers, isRetailer, etc.) passed at
// the route call sites below. Type the bindings themselves to relax it.
const WrappedCatalogManagerCatalog: React.ComponentType<any> = withPOCEnabled(CatalogManagerCatalog as any);
const WrappedCatalogManagerProductDetails: React.ComponentType<any> = withPOCEnabled(CatalogManagerProductDetails as any);
const WrappedCatalogManagerSettings: React.ComponentType<any> = withPOCEnabled(CatalogManagerSettings as any);
const WrappedCatalogManagerSettingsTaxonmy: React.ComponentType<any> = withPOCEnabled(CatalogManagerSettingsTaxonomy as any);
const WrappedCatalogManagerSettingsGeneral: React.ComponentType<any> = withPOCEnabled(CatalogManagerSettingsGeneral as any);
const WrappedCatalogManagerSettingsAutoApprove: React.ComponentType<any> = withPOCEnabled(CatalogManagerSettingsAutoApprove as any);
const WrappedCatalogManagerSettingsAutoIgnore: React.ComponentType<any> = withPOCEnabled(CatalogManagerSettingsAutoIgnore as any);
const WrappedCatalogManagerSettingsConnector: React.ComponentType<any> = withPOCEnabled(CatalogManagerSettingsConnector as any);
const WrappedCatalogManagerSettingsBulkActions: React.ComponentType<any> = withPOCEnabled(CatalogManagerSettingsBulkActions as any);
const WrappedCatalogManagerSettingsChannelSettings: React.ComponentType<any> = withPOCEnabled(CatalogManagerSettingsChannelSettings as any);
const WrappedCatalogManagerAdvancedExport: React.ComponentType<any> = withPOCEnabled(CatalogManagerAdvancedExport as any);
const WrappedCatalogManagerAdvancedImport: React.ComponentType<any> = withPOCEnabled(CatalogManagerAdvancedImport as any);
const WrappedCatalogManagerProductAdvancedImport: React.ComponentType<any> = withPOCEnabled(CatalogManagerProductAdvancedImport as any);
const BillableRetailerPMC = billableOnly(retailerOnly(PartnerManagementCenter));

interface AppState {
    sessionExpired: boolean;
    showPermissionError: boolean;
    companySwitched: boolean;
    showUpdate: boolean;
    showSignalr: boolean;
    redirect?: string | null;
    [key: string]: any;
}

const App = (props: any) => {
    const menuRef = useRef<any>(null);
    const lastRedirectRef = useRef<any>(undefined);

    const [state, setStateRaw] = useState<AppState>(() => {
        const base: AppState = { sessionExpired: false, showPermissionError: false, companySwitched: false, showUpdate: false, showSignalr: false };
        var redirect = Auth.getValidToken();
        if (redirect) {
            base.redirect = redirect;
        }
        return base;
    });

    // Mirror the latest committed state so callbacks/intervals registered once
    // (in the mount effect) always read current values, matching class
    // `this.state` semantics.
    const stateRef = useRef(state);
    stateRef.current = state;

    // Partial-merge setState equivalent of the class API.
    const setState = useCallback((partial: Partial<AppState>) => {
        setStateRaw(prev => ({ ...prev, ...partial }));
    }, []);

    const showUpdate = useCallback(() => {
        setState({ showUpdate: true });
    }, [setState]);

    const reload = useCallback(() => {
        window.location.reload();
    }, []);

    const identifyExternalServices = useCallback((company: any) => {
        const win: any = window;
        try {
            win.zE.identify({ name: Auth.getName(), email: Auth.getEmail() });
        } catch (e) {
            // do nothing
        }
        try {
            let userId = Auth.getUserId();
            let email = Auth.getEmail();
            if (email && email.toLowerCase().endsWith('@logicbroker.com')) {
                userId = 'lb:' + userId;
            }
            win.pendo.initialize({
                visitor: {
                    id: userId,
                    email: email,
                    full_name: Auth.getName()
                },
                account: {
                    id: Auth.getAccountNumber(),
                    name: company.CompanyName,
                    is_paying: company.IsBillable,
                    retailer: company.IsVendor,
                    tags: company.Tags,
                    type: company.Type,
                    partners: company.Partners
                }
            });
        } catch {
            // do nothing
        }
        try {
            let name = Auth.getName().split(" ");
            let firstName = name[0];
            let lastName = name.slice(1).join(" ");
            win.FrigadeAssistant = win.FrigadeAssistant ||
                function (config: any) {
                    (win.FrigadeAssistant.q = win.FrigadeAssistant.q || []).push(config);
                };
            win.FrigadeAssistant.on = win.FrigadeAssistant.on ||
                function (event: any, handler: any) {
                    (win.FrigadeAssistant.on.q = win.FrigadeAssistant.on.q || []).push({
                        event,
                        handler,
                    });
                };
            win.FrigadeAssistant({
                apiKey: 'fr_public_key_DUwFy4CAKAR8WJrP424WqqpyQtMmWSk1Alqt7HeysFqbSRBvV64bcjRo9zIXToou',
                apiUrl: 'https://api.frigade.ai',
                userId: Auth.getUserId(),
                assistantId: company.IsVendor ? 'assistant_0XzKD7eq8ZRb' : undefined,
                userProperties: {
                    firstName: firstName,
                    lastName: lastName,
                    email: Auth.getEmail(),
                    role: company.IsVendor ? 'RETAILER' : 'SUPPLIER',
                },
            });
        } catch {
            // do nothing
        }
    }, []);

    const reconnectSignalr = useCallback(() => {
        if (stateRef.current.signalr) {
            stateRef.current.signalr.connection.start();
        }
    }, []);

    const connectSignalr = useCallback(() => {
        let connection = HubConnection(Config.api + "/signalr");
        let hubProxy = connection.createHubProxy("signalrHub");
        connection.stateChanged((state) => {
            let token = Auth.getStoredToken();
            if (state.oldState !== state.newState && state.newState === 1 && Auth.isValidToken(token)) {
                hubProxy.invoke('register', token);
                setState({ signalrRetryCount: 0 });
            }
            if (state.newState === 4 && !stateRef.current.showSignalr && (!stateRef.current.signalrRetryCount || stateRef.current.signalrRetryCount < 3) && Auth.isValidToken(token)) {
                // attempt to reconnect if we still have a valid token
                setState({ signalrRetryCount: 1 + (stateRef.current.signalrRetryCount || 0) });
                reconnectSignalr();
            } else if (state.newState === 4 && !stateRef.current.showSignalr && Auth.isValidToken(token)) {
                setState({ showSignalr: true });
            } else if (state.newState === 1 && stateRef.current.showSignalr) {
                setState({ showSignalr: false, signalrRetryCount: 0 });
            }
        });
        connection.start().fail(function () { console.log('Could not connect to real time update service.'); });
        return hubProxy;
    }, [setState, reconnectSignalr]);

    const getEmbeddedReport = useCallback(async () => {
        try {
            var embeddedReport = (await axios.get(Config.api + "/odata/Company/Functions.GetEmbeddedReport")).data;
            var embed = null;
            if (embeddedReport.EmbedToken) {
                embed = embeddedReport;
            }
            return embed;
        } catch {
            return null;
        }
    }, []);

    const getTieredReport = useCallback(async () => {
        try {
            var embeddedReport = (await axios.get(Config.api + "/odata/Company/Functions.GetTieredReport")).data;
            var embed = null;
            if (embeddedReport.EmbedToken) {
                embed = embeddedReport;
            }
            return embed;
        } catch {
            return null;
        }
    }, []);

    // LB-480: current ThoughtSpot embed token (GetCompanyInfo carries it as ThoughtSpotEmbed).
    const getThoughtSpotEmbed = useCallback(async () => {
        try {
            var res = (await axios.get(Config.api + "/odata/Company/Functions.GetCompanyInfo")).data;
            var embed = null;
            if (res && res.ThoughtSpotEmbed && res.ThoughtSpotEmbed.EmbedToken) {
                embed = res.ThoughtSpotEmbed;
            }
            return embed;
        } catch {
            return null;
        }
    }, []);

    const requestNewAuthToken = useCallback(async (newCoId?: number) => {
        if (typeof (newCoId) !== 'undefined') {
            var newToken = (await axios.post(Config.oauth + '/user/switchactivecompany?coid=' + newCoId)).data.token;
            var oldToken = Auth.getStoredToken();
            Auth.setStoredToken(newToken);
            if (oldToken !== newToken) {
                resetThoughtSpotAuth();
            }
            setState({ coid: parseInt(Auth.getAccountNumber()) });
            if (stateRef.current.signalr && oldToken) {
                try {
                    stateRef.current.signalr.invoke('unregister', oldToken);
                } catch (e) {
                    // do nothing
                }
            }
        }
    }, [setState]);

    const relogin = useCallback(() => {
        Auth.setStoredToken(null);
        resetThoughtSpotAuth();
        window.location.reload();
    }, []);

    const loadAgreements = useCallback(async (updateState?: boolean): Promise<any> => {
        let agreements: any = [];
        try {
            agreements = (await axios.get(Config.oauth + "/user/agreements")).data;
        } catch (e) {
            agreements = undefined;
        }
        if (updateState) {
            setState({ agreements });
        }
        return agreements;
    }, [setState]);

    const resetState = useCallback(async (newCoId?: number, preventSwitch?: boolean) => {
        if (typeof (newCoId) !== 'undefined' && document.activeElement) {
            (document.activeElement as HTMLElement).blur();
        }
        if (!preventSwitch) {
            await requestNewAuthToken(newCoId);
        }
        var res: any = null;
        try {
            res = (await axios.get(Config.api + "/odata/Company/Functions.GetCompanyInfo")).data;
        } catch (e: any) {
            let error = null;
            if (e.response && e.response.data) {
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            if (!error && !e.response) {
                error = "Request failed: " + e.message;
            }
            if (e.response && e.response.status === 401 && error && error.indexOf("disabled") > 0) {
                setState({ ready: true, disabled: true, errorMessage: error, coid: parseInt(Auth.getAccountNumber()), hasApiPermission: Auth.hasPermission("api/manage") });
            } else {
                setState({ ready: true, error: true, errorMessage: error, statusCode: e.response ? e.response.status : null, disabled: false, coid: parseInt(Auth.getAccountNumber()), hasApiPermission: Auth.hasPermission("api/manage") });
            }
            return;
        }
        loadAgreements(true);
        if (stateRef.current.signalr) {
            try {
                stateRef.current.signalr.invoke('register', Auth.getStoredToken());
            } catch (e) {
                // do nothing
            }
        }
        Auth.CompanyName = res.CompanyName;
        Auth.CompanyTags = res.Tags;
        let helpDesks = (res.Helpdesks || []).sort((a, b) => a.CompanyName > b.CompanyName ? 1 : -1);
        let flags = {
            productEnabled: res.ProductEnabled, billingEnabled: res.BillingEnabled, pocEnabled: res.IsPOCEnabled,
            pocRetailers: res.POCRetailers, catalogEnabled: res.CatalogEnabled, isRetailer: res.IsVendor,
            isBillable: res.IsBillable, inNetwork: res.InNetwork, onboardingEnabled: res.OnboardingEnabled, tier: res.Tier,
            financialStatus: res.FinancialStatus, financingEnabled: res.FinancingEnabled
        };
        let nextState = {
            ready: true, disabled: false, error: false, companyName: res.CompanyName, coid: res.CoId, helpDesks: helpDesks,
            embeddedReport: res.ReportEmbed, tieredReport: res.TieredReportEmbed, thoughtSpotEnabled: isThoughtSpotLiveboardEnabled(res), thoughtSpotEmbed: res.ThoughtSpotEmbed, thoughtSpotTokenIssuedAt: Date.now(), thoughtSpotSpotterEnabled: isThoughtSpotSpotterEnabled(res), hasApiPermission: Auth.hasPermission("api/manage"), flags
        };
        setState(nextState);
        if (typeof (newCoId) !== 'undefined') {
            var hostAndQuery = window.location.toString().split("//")[1];
            var pathAndQuery = hostAndQuery.substring(hostAndQuery.indexOf('/'));
            new StorageHelper().remove("lb-recents");
            window.dispatchEvent(new StorageEvent('storage', { key: 'lb-recents' }));
            setState({ redirect: '/refresh' + pathAndQuery });
        }
        identifyExternalServices(res);
        clearHelperCache();
        try {
            if (flags.financingEnabled && !document.querySelector("#kanmonScripts")) {
                let script = document.createElement("script");
                script.id = "kanmonScripts";
                script.src = Config.production ? "https://cdn.kanmon.dev/scripts/v2/kanmon-connect.js" : "https://cdn.sandbox.kanmon.dev/scripts/v2/kanmon-connect.js";
                document.head.appendChild(script);
            }
        } catch { }
    }, [requestNewAuthToken, loadAgreements, identifyExternalServices, setState]);

    const switchBack = useCallback(async () => {
        setState({ disableSwitchButtons: true });
        await requestNewAuthToken(stateRef.current.coid);
    }, [setState, requestNewAuthToken]);

    const continueWithNewCompany = useCallback(async () => {
        setState({ disableSwitchButtons: true });
        await resetState(Number(Auth.getAccountNumber()), true);
        menuRef.current.selectValue(Number(Auth.getAccountNumber()));
    }, [setState, resetState]);

    const setTitle = useCallback((title: string, includeInRecents?: boolean) => {
        title = title ? 'Logicbroker: ' + title : 'Logicbroker';
        menuRef.current && menuRef.current.setTitle(title, includeInRecents);
    }, []);

    // Constructor axios interceptors + polling intervals, plus componentDidMount.
    useEffect(() => {
        const requestInterceptor = axios.interceptors.request.use(function (config: any) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers['Authorization'] = 'Bearer ' + Auth.getStoredToken();
            return config;
        });

        const responseInterceptor = axios.interceptors.response.use(null, function (error: any) {
            if (!error.response) {
                // uh oh no response, might have lost connection
                return Promise.reject(error);
            }
            if (error.response.status === 401 && error.config.url.indexOf(Config.api) === 0) {
                Auth.setStoredToken(null);
                resetThoughtSpotAuth();
                Auth.getValidToken();
            }
            if (error.response.status === 403 && error.config.url.indexOf(Config.api) === 0) {
                setState({ showPermissionError: true });
            }
            return Promise.reject(error);
        });

        const sessionInterval = window.setInterval(function () {
            if (!Auth.isValidToken() && !stateRef.current.sessionExpired) {
                setState({ sessionExpired: true, companySwitched: false });
            } else if (stateRef.current.sessionExpired && Auth.isValidToken()) {
                setState({ sessionExpired: false });
                if (stateRef.current.coid !== parseInt(Auth.getAccountNumber())) {
                    reload();
                } else if (stateRef.current.signalr) { // re-register signalr if we have a new token
                    try {
                        reconnectSignalr();
                    } catch (e) {
                        // do nothing
                    }
                }
            } else if (!isNaN(stateRef.current.coid) && stateRef.current.coid !== parseInt(Auth.getAccountNumber()) && !stateRef.current.companySwitched && !stateRef.current.sessionExpired) {
                setState({ companySwitched: true, disableSwitchButtons: false });
            } else if (stateRef.current.coid === parseInt(Auth.getAccountNumber()) && stateRef.current.companySwitched) {
                setState({ companySwitched: false });
            }
        }, 2 * 1000);
        const embeddedReportInterval = window.setInterval(async function () {
            if (Auth.isValidToken() && stateRef.current.embeddedReport && new Date(stateRef.current.embeddedReport.Expiration) < new Date()) {
                var embed = await getEmbeddedReport();
                setState({ embeddedReport: embed });
            }
        }, 30 * 1000);
        const tieredReportInterval = window.setInterval(async function () {
            if (Auth.isValidToken() && stateRef.current.tieredReport && new Date(stateRef.current.tieredReport.Expiration) < new Date()) {
                var embed = await getTieredReport();
                setState({ tieredReport: embed });
            }
        }, 30 * 1000);
        // LB-480: keep the ThoughtSpot token fresh app-wide, like the Power BI timers above.
        // Without this the embed token is frozen for the whole SPA session and the SDK
        // eventually paints its "Not logged in" message over the embed until a page reload.
        const thoughtSpotTokenInterval = window.setInterval(async function () {
            if (Auth.isValidToken() && stateRef.current.thoughtSpotEmbed &&
                shouldRefreshThoughtSpotToken(stateRef.current.thoughtSpotEmbed, Date.now(), stateRef.current.thoughtSpotTokenIssuedAt)) {
                var embed = await getThoughtSpotEmbed();
                if (embed && embed.EmbedToken) {
                    setState({ thoughtSpotEmbed: embed, thoughtSpotTokenIssuedAt: Date.now() });
                }
            }
        }, 30 * 1000);

        document.title = "Logicbroker";
        try {
            window.addEventListener('react_spa_update', showUpdate, false);
        } catch { }
        if (Auth.isValidToken() && !stateRef.current.ready) {
            (async () => {
                await resetState();
                var companies = (await axios.get(Config.oauth + '/user/getaccessiblecompanies')).data;
                setState({ companies: companies, signalr: connectSignalr() });
            })();
        }

        return () => {
            window.clearInterval(sessionInterval);
            window.clearInterval(embeddedReportInterval);
            window.clearInterval(tieredReportInterval);
            window.clearInterval(thoughtSpotTokenInterval);
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
            try {
                window.removeEventListener('react_spa_update', showUpdate, false);
            } catch { }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentDidUpdate: clear a pending redirect once the app is ready.
    useEffect(() => {
        if (state.redirect && state.ready) {
            setState({ redirect: null });
        }
    }, [state.redirect, state.ready, setState]);

    {
        let USE_POC_ONBOARDING_STYLES = true;
        let USE_AGENTIC_COMMERCE = Auth.CompanyTags && Auth.CompanyTags.includes("demo");
        let USE_THOUGHTSPOT = state.thoughtSpotEnabled === true;
        let USE_THOUGHTSPOT_SPOTTER = state.thoughtSpotSpotterEnabled === true;

        lastRedirectRef.current = state.redirect;
        let flags = state.flags;
        return (
            <ThemeProvider theme={theme}>
                <BrowserRouter>
                    <Fragment>
                        {state.ready && state.disabled && <div className='disabledHolder'>
                            <img src={logo} alt="Logicbroker" className='disabledLogo' />
                            <Card className='disabledCard'>
                                <CardContent>
                                    <div className='disabledHeader'>SORRY</div>
                                    <p>{`It looks like your company is not enabled in ${Config.production ? 'production' : 'stage'}.`}</p>
                                    {Config.production && <p>If you are new to Logicbroker you may need to log into our stage environment instead, please <a href="https://stageportal.logicbroker.com">click here</a>.</p>}
                                    <p>Please contact <a href='mailto:support@logicbroker.com'>support@logicbroker.com</a> if you have any questions.</p>
                                    {state.coid && state.companies && state.companies.length > 1 && <ExtendedSelect style={{ width: '100%', display: 'block' }} variant='outlined' placeholder='Select company...' onChange={resetState} value={state.coid} options={state.companies.map((i) => { return { label: i.Item2, value: i.Item1 }; })} />}
                                </CardContent>
                            </Card>
                        </div>}
                        {state.ready && state.error && <div className='disabledHolder'>
                            <img src={logo} alt="Logicbroker" className='disabledLogo' />
                            <Card className='disabledCard'>
                                <CardContent>
                                    <div className='disabledHeader'>SORRY</div>
                                    <p>It looks like we can't connect to the Logicbroker API at <a href={Config.api} target='_blank' rel='noopener noreferrer'>{Config.api.split('/')[2]}</a> right now.</p>
                                    <p>Please try again. If the situation persists please contact <a href='mailto:support@logicbroker.com'>support@logicbroker.com</a> for assistance.</p>
                                    {state.statusCode && <p>{`Error code: ${state.statusCode}`}</p>}
                                    {state.errorMessage && <p>{state.errorMessage}</p>}
                                    <div style={{ width: '100%', textAlign: 'center' }}><Button variant='contained' color='primary' onClick={relogin}>Try again</Button></div>
                                </CardContent>
                            </Card>
                        </div>}
                        {state.ready && !state.disabled && !state.error ? <div className="App">
                            <MenuContainer
                                menuRef={menu => menuRef.current = menu}
                                onCompanySwitch={resetState}
                                coid={state.coid}
                                companyName={state.companyName}
                                USE_AGENTIC_COMMERCE={USE_AGENTIC_COMMERCE}
                                {...flags}
                                helpDesks={state.helpDesks}
                                companies={state.companies}
                                hasAdvancedReport={hasReport(state.embeddedReport)}
                                hasThoughtSpotLiveboard={USE_THOUGHTSPOT}
                                hasThoughtSpotSpotter={USE_THOUGHTSPOT_SPOTTER}
                            >

                                <div className='app-content'>
                                    {state.redirect ? <Redirect to={state.redirect} /> : ''}
                                    <Route render={({ location }) => (
                                      <ErrorBoundary routeKey={location.pathname + location.search + location.hash}>
                                    <Switch>
                                        <Route exact path="/" render={(props) => <Dashboard {...props} setTitle={setTitle} isRetailer={flags.isRetailer} tier={flags.tier} companyName={state.companyName} financingEnabled={flags.financingEnabled} financialStatus={flags.financialStatus} />} />
                                        <Route exact path="/home" render={() => <Redirect to="/" />} />
                                        <Route exact path="/search" render={(props) => <Search {...props} setTitle={setTitle} signalr={state.signalr} />} />

                                        <Route exact path="/order-management"
                                            render={(props) => USE_POC_ORDER_STYLES
                                                ? <OrderListV2 {...props} setTitle={setTitle} signalr={state.signalr} isRetailer={flags.isRetailer} companyName={state.companyName} financingEnabled={flags.financingEnabled} financialStatus={flags.financialStatus} />
                                                : <OrderList {...props} setTitle={setTitle} signalr={state.signalr} isRetailer={flags.isRetailer} companyName={state.companyName} financingEnabled={flags.financingEnabled} financialStatus={flags.financialStatus} />
                                            }
                                        />

                                        <Route exact path="/order-management/order-details" render={(props) => <OrderDetails {...props} setTitle={setTitle} isBillable={flags.isBillable} isRetailer={flags.isRetailer} signalr={state.signalr} />} />
                                        <Route exact path="/order-management/invoice-details" render={(props) => <InvoiceDetails {...props} setTitle={setTitle} isBillable={flags.isBillable} signalr={state.signalr} />} />
                                        <Route exact path="/order-management/ack-details" render={(props) => <AckDetails {...props} setTitle={setTitle} isBillable={flags.isBillable} signalr={state.signalr} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/order-management/return-details" render={(props) => <ReturnDetails {...props} setTitle={setTitle} isBillable={flags.isBillable} signalr={state.signalr} />} />
                                        <Route exact path="/order-management/shipment-details" render={(props) => <ShipmentDetails {...props} setTitle={setTitle} isBillable={flags.isBillable} signalr={state.signalr} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/order-management/fulfill-cancel" component={ShipMultiple} />
                                        <Route exact path="/order-management/invoice" component={InvoiceMultiple} />
                                        <Route exact path="/order-management/accept" component={AckMultiple} />

                                        <Route exact path="/advanced-product-management/search" render={(props) => <InventoryManagementCenter {...props} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} tieredReport={state.tieredReport} {...props} />} />
                                        <Route exact path="/advanced-product-management" render={(props) => <InventoryManagementCenter {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} tieredReport={state.tieredReport} {...props} />} />
                                        {(flags.isBillable) && <Route exact path="/advanced-product-management/settings" render={(props) => <InventorySettings {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        <Route exact path="/advanced-product-management/export" render={(props) => <InventoryExport {...props} isRetailer={flags.isRetailer} setTitle={setTitle} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />
                                        {(Auth.hasPermission("inventory/manage")) && <Route exact path="/advanced-product-management/import" render={(props) => <InventoryImport {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}

                                        <Route exact path="/product-feeds" render={(props) => <ProductManagement {...props} productEnabled={flags.productEnabled} signalr={state.signalr} isRetailer={flags.isRetailer} isBillable={flags.isBillable} />} />
                                        <Route exact path="/product-feeds/rules" render={(props) => <RetailerProductRules {...props} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/product-feeds/specification" render={(props) => <ProductSpec {...props} productEnabled={flags.productEnabled} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/catalog" render={(props) => <Catalog catalogEnabled={flags.catalogEnabled} signalr={state.signalr} {...props} />} />
                                        <Route exact path="/catalog-attributes" render={(props) => <CatalogAttributes catalogEnabled={flags.catalogEnabled} {...props} />} />
                                        <Route exact path="/catalog-export" render={(props) => <CatalogExport catalogEnabled={flags.catalogEnabled} isRetailer={flags.isRetailer} {...props} />} />
                                        <Route exact path="/catalog-import" render={(props) => <CatalogImport catalogEnabled={flags.catalogEnabled} isRetailer={flags.isRetailer} {...props} />} />
                                        <Route exact path="/catalog-item" render={(props) => <CatalogItem catalogEnabled={flags.catalogEnabled} {...props} />} />
                                        <Route exact path="/catalog/categories" render={(props) => <Categories {...props} />} />
                                        <Route exact path="/catalog/setup" render={(props) => <CatalogWizard {...props} />} />
                                        <Route exact path="/catalog/import-export-settings" render={(props) => <ProductCatalogProperties catalogEnabled={flags.catalogEnabled} isBillable={flags.isBillable} {...props} />} />
                                        {USE_AGENTIC_COMMERCE && (
                                            <Route exact path="/agentic-commerce" render={(props) => <AgenticCommerceOverview {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />}/>
                                        )}
                                        {USE_AGENTIC_COMMERCE && (
                                            <Route exact path="/agentic-commerce/paypal" render={(props) => <PayPalDashboard {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />}/>
                                        )}
                                        {USE_AGENTIC_COMMERCE && (
                                            <Route exact path="/agentic-commerce/stripe" render={(props) => <StripeDashboard {...props} setTitle={setTitle} />}/>
                                        )}

                                        <Route exact path="/productonboardingcenter/catalog" render={(props) => <WrappedCatalogManagerCatalog setTitle={setTitle} pocEnabled={flags.pocEnabled} retailers={flags.pocRetailers} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />
                                        <Route exact path="/productonboardingcenter/product" render={(props) => <WrappedCatalogManagerProductDetails setTitle={setTitle} pocEnabled={flags.pocEnabled} retailers={flags.pocRetailers} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />
                                        <Route exact path="/partnermanagementcenter" render={(props) => <BillableRetailerPMC isRetailer={flags.isRetailer} setTitle={setTitle} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />
                                        {flags.isRetailer && <Route exact path="/partnermanagementcenter/partner" render={(props) => <PartnerManager setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/partnermanagementcenter/settings" render={(props) => <PartnerManagementSettings setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/partnermanagementcenter/settings/suppliergroups" render={(props) => <SupplierGroupsSettings setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/productonboardingcenter/settings" render={(props) => <WrappedCatalogManagerSettings setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/productonboardingcenter/settings/taxonomy" render={(props) => <WrappedCatalogManagerSettingsTaxonmy setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/productonboardingcenter/settings/general" render={(props) => <WrappedCatalogManagerSettingsGeneral setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/productonboardingcenter/settings/auto-approve" render={(props) => <WrappedCatalogManagerSettingsAutoApprove setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/productonboardingcenter/settings/auto-ignore" render={(props) => <WrappedCatalogManagerSettingsAutoIgnore setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/productonboardingcenter/settings/connector" render={(props) => <WrappedCatalogManagerSettingsConnector setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {(flags.isRetailer && Auth.hasPermission("inventory/manage")) && <Route exact path="/productonboardingcenter/settings/bulk-actions" render={(props) => <WrappedCatalogManagerSettingsBulkActions setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {(!flags.isRetailer && flags.pocRetailers.length > 0) && <Route exact path="/productonboardingcenter/settings/channel-settings" render={(props) => <WrappedCatalogManagerSettingsChannelSettings setTitle={setTitle} pocEnabled={flags.pocEnabled} retailers={flags.pocRetailers} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        <Route exact path="/productonboardingcenter/export/:type" render={(props) => <WrappedCatalogManagerAdvancedExport setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />
                                        <Route exact path="/productonboardingcenter/export" render={(props) => <WrappedCatalogManagerAdvancedExport pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />
                                        {flags.isRetailer && <Route exact path="/productonboardingcenter/import/products" render={(props) => <WrappedCatalogManagerProductAdvancedImport pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/productonboardingcenter/import/:type" render={(props) => <WrappedCatalogManagerAdvancedImport setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {flags.isRetailer && <Route exact path="/productonboardingcenter/import" render={(props) => <WrappedCatalogManagerAdvancedImport setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} signalr={state.signalr} {...props} />} />}
                                        {!flags.isRetailer && <Route exact path="/productonboardingcenter/import" render={(props) => <WrappedCatalogManagerProductAdvancedImport setTitle={setTitle} pocEnabled={flags.pocEnabled} isRetailer={flags.isRetailer} isBillable={flags.isBillable} retailers={flags.pocRetailers} signalr={state.signalr} {...props} />} />}

                                        <Route exact path="/settings/edi/report" component={EdiReport} />
                                        <Route exact path="/settings/edi" render={props => <EdiSettings {...props} setTitle={setTitle} />} />
                                        <Route exact path="/attachment-list"
                                            render={(props) => USE_POC_ATTACHMENT_STYLES
                                                ? <AttachmentsV2 {...props} setTitle={setTitle} />
                                                : <Attachments {...props} setTitle={setTitle} />
                                            }
                                        />
                                        <Route exact path="/order-management/advanced-export" render={(props) => <AdvancedExport setTitle={setTitle} isRetailer={flags.isRetailer} companyName={state.companyName} coid={state.coid} />} />
                                        <Route exact path="/order-management/advanced-import" render={(props) => <AdvancedImport setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} />} />
                                        <Route exact path="/event-management"
                                            render={props => USE_POC_EVENT_STYLES
                                                ? <EventManagementV2 {...props} setTitle={setTitle} />
                                                : <EventManagement {...props} setTitle={setTitle} />
                                            }
                                        />
                                        <Route exact path="/message-center" render={(props) => <MessageList {...props} setTitle={setTitle} signalr={state.signalr} />} />
                                        <Route exact path="/message-center/thread" render={(props) => <Thread {...props} setTitle={setTitle} signalr={state.signalr} />} />
                                        <Route exact path="/scorecards" render={(props) => <Scorecards {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        {flags.isRetailer && <Route exact path="/scorecards/options" render={props => <ScoreCardProperties {...props} setTitle={setTitle} />} />}
                                        <Route exact path="/document-standards" render={(props) => <DocumentStandards {...props} setTitle={setTitle} />} />
                                        <Route exact path="/failed-documents" render={(props) => <DocumentFailures {...props} setTitle={setTitle} />} />
                                        <Route exact path="/failed-documents/list" render={(props) => <FailedDocumentList {...props} setTitle={setTitle} />} />
                                        <Route exact path="/reports" render={() => <Redirect to="/" />} />
                                        <Route exact path="/advanced-reports" render={(props) => <AdvancedReports {...props} setTitle={setTitle} companyName={state.companyName} embeddedReport={state.embeddedReport} thoughtSpotEnabled={USE_THOUGHTSPOT} thoughtSpotEmbed={state.thoughtSpotEmbed} />} />
                                        <Route exact path="/ask-data" render={(props) => USE_THOUGHTSPOT_SPOTTER ? <AskData {...props} setTitle={setTitle} thoughtSpotEmbed={state.thoughtSpotEmbed} /> : <Redirect to="/" />} />
                                        <Route exact path="/testing" render={props => <TestingOverview {...props} setTitle={setTitle} />} />
                                        <Route exact path="/testing/cases" render={props => <TestCases {...props} setTitle={setTitle} />} />
                                        <Route exact path="/profile/account-information" render={(props) => <AccountInformation {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} billingEnabled={flags.billingEnabled} />} />
                                        <Route exact path="/billing" render={(props) => <WrappedBillingDashboard {...props} billingEnabled={flags.billingEnabled} />} />
                                        <Route exact path="/billing/subscription" render={(props) => <WrappedSubscriptionDetail {...props} billingEnabled={flags.billingEnabled} />} />
                                        <Route exact path="/billing/subscription/invoice" render={(props) => <WrappedSubscriptionInvoiceDetail {...props} billingEnabled={flags.billingEnabled} />} />
                                        <Route exact path="/profile/product-showcase" render={(props) => <ProductProfile {...props} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/profile" render={(props) => <UserProfile {...props} setTitle={setTitle} />} />
                                        <Route exact path="/profile/notifications" render={(props) => <Notifications {...props} setTitle={setTitle} companyName={state.companyName} coid={state.coid} isBillable={flags.isBillable} />} />
                                        <Route exact path="/settings/monitoring" render={(props) => <Monitoring {...props} setTitle={setTitle} />} />
                                        <Route exact path="/settings/documents" render={(props) => <DocumentSettings {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/profile/api-authentication" render={(props) => <ApiAuthentication {...props} setTitle={setTitle} hasPermission={state.hasApiPermission} isBillable={flags.isBillable} />} />
                                        <Route exact path="/logos" component={LogoManager} />
                                        <Route exact path="/shipment-options" render={(props) => <ShipmentOptions {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/profile/userlogins" render={(props) => <UserManagement {...props} setTitle={setTitle} />} />
                                        <Route exact path="/connections" render={(props) => <Connections {...props} setTitle={setTitle} isBillable={flags.isBillable} />} />
                                        <Route exact path="/settings/sourcing" render={(props) => <Sourcing {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/settings/default-return-addresses" render={(props) => <SupplierAddresses {...props} setTitle={setTitle} isRetailer={flags.isRetailer} addressType='Return' addressTitle='Return' />} />
                                        <Route exact path="/settings/default-remit-to-addresses" render={(props) => <SupplierAddresses {...props} setTitle={setTitle} isRetailer={flags.isRetailer} addressType='RemitTo' addressTitle='Remit To' />} />
                                        <Route exact path="/settings/default-ship-from-addresses" render={(props) => <SupplierAddresses {...props} setTitle={setTitle} isRetailer={flags.isRetailer} addressType='ShipFrom' addressTitle='Ship From' />} />
                                        <Route exact path="/settings/default-payment-terms" render={(props) => <SupplierPaymentTerms {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/settings/default-ack-properties" render={(props) => <SupplierAckProperties {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} />} />
                                        <Route exact path="/settings/default-return-properties" render={(props) => <SupplierReturnProperties {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/settings/default-shipment-properties" render={(props) => <SupplierShipmentProperties {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} />} />
                                        <Route exact path="/settings/custom-lookups" render={(props) => <BillableCustomLookups {...props} setTitle={setTitle} isBillable={flags.isBillable} />} />
                                        <Route exact path="/payments/account" render={(props) => <PaymentAccountView {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/payments/dashboard" render={(props) => <PaymentDashboard {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/payments/transfers" render={(props) => <PaymentTransfers {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/payments/transfer" render={(props) => <TransferDetails {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/scheduled-tasks"
                                            render={(props) => USE_POC_SCHEDULED_TASKS_STYLES
                                                ? <ScheduledTasksV2 {...props} setTitle={setTitle} signalr={state.signalr} />
                                                : <ScheduledTasks {...props} setTitle={setTitle} signalr={state.signalr} />
                                            }
                                        />
                                        {oauthRoutes}
                                        <Route exact path="/connected-commerce-network" render={(props) => <NetworkCompanies {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        <Route exact path="/connected-commerce-network/profile" render={(props) => <CompanyProfile {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />

                                        {USE_AGENTIC_COMMERCE && (
                                            <Route exact path="/agentic-commerce" render={(props) => <AgenticCommerceOverview {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />} />
                                        )}

                                        <Route exact path="/onboarding"
                                            render={(props) => USE_POC_ONBOARDING_STYLES
                                                ? <BillableRetailerOnboardingV2 {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} />
                                                : <BillableRetailerOnboarding {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} />
                                            }
                                        />
                                        <Route exact path="/onboarding/integration"
                                            render={(props) => USE_POC_ONBOARDING_STYLES
                                                ? <OnboardingDetailsV2 {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />
                                                : <OnboardingDetails {...props} setTitle={setTitle} isRetailer={flags.isRetailer} />
                                            }
                                        />

                                        <Route exact path="/onboarding/settings"
                                            render={(props) => USE_POC_ONBOARDING_STYLES
                                                ? <BillableRetailerOnboardingSettingsV2 {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} />
                                                : <BillableRetailerOnboardingSettings {...props} setTitle={setTitle} isRetailer={flags.isRetailer} isBillable={flags.isBillable} />
                                            }
                                        />

                                        <Route path="/storage" component={AttachmentStorage} />
                                        <Route path="/areas/logicbroker/picklist.ashx" component={(props) => <LabelRedirect {...props} type='picklist' />} />
                                        <Route path="/areas/logicbroker/shippinglabel.ashx" component={(props) => <LabelRedirect {...props} type='gs1label' />} />
                                        <Route component={NotFound} />
                                    </Switch>
                                      </ErrorBoundary>
                                    )} />
                                    <Refresh path="/refresh" />
                                    <Agreements loadAgreements={loadAgreements} inNetwork={flags.inNetwork} isBillable={flags.isBillable} isRetailer={flags.isRetailer} agreements={state.agreements} companyName={state.companyName} />
                                    <Dialog open={state.sessionExpired}>
                                        <Toolbar className='lbtoolbar' style={{margin: 0}}>Session Expired</Toolbar>
                                        <DialogContent>
                                            <DialogContentText id="alert-dialog-description">
                                                {'Your session has expired. Please sign in again to continue.'}
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={() => window.open(Auth.getLoginUrl("/login"))} variant="text" color="primary">Sign In</Button>
                                        </DialogActions>
                                    </Dialog>
                                    <Dialog open={!state.sessionExpired && state.companySwitched}>
                                        <Toolbar className='lbtoolbar'>Company Switched</Toolbar>
                                        <DialogContent>
                                            <DialogContentText id="alert-dialog-description">
                                                {'You have switched to another company in a different tab or browser window. Only one active company is allowed at a time.'}
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button variant="text" disabled={state.disableSwitchButtons} onClick={switchBack} color="primary">Switch back</Button>
                                            <Button variant="text" disabled={state.disableSwitchButtons} onClick={continueWithNewCompany} color="primary">Continue with active company</Button>
                                        </DialogActions>
                                    </Dialog>
                                    <Dialog open={state.showPermissionError} onClose={() => setState({ showPermissionError: false })}>
                                        <Toolbar className='lbtoolbar'>Permission Denied</Toolbar>
                                        <DialogContent>
                                            <DialogContentText id="alert-dialog-description">
                                                {'You are not allowed to perform that action. Please contact your account administrator for access.'}
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button variant="text" onClick={() => setState({ showPermissionError: false })} color="primary">Ok</Button>
                                        </DialogActions>
                                    </Dialog>
                                    <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                        open={state.showUpdate}
                                        onClose={() => { setState({ showUpdate: false }) }}
                                        message={'We\'ve made some changes. Please refresh the page to get the latest version.'}
                                        action={[
                                            <Button key="refresh" color="primary" size="small" onClick={() => window.location.reload()}>
                                                Refresh
                                            </Button>,
                                            <IconButton
                                                key="close"
                                                onClick={() => { setState({ showUpdate: false }) }}
                                                size="large">
                                                <CloseIcon />
                                            </IconButton>,
                                        ]}
                                        slotProps={{
                                            content: { style: { backgroundColor: 'white', color: 'black' } }
                                        }}
                                    />
                                    <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                        open={state.showSignalr && !state.showUpdate}
                                        message={'Connection to the real-time update service has been lost. Please reconnect or reload the page.'}
                                        action={[
                                            <Button key="reconnect" color="primary" size="small" onClick={reconnectSignalr}>
                                                Reconnect
                                            </Button>,
                                            <IconButton
                                                key="close"
                                                onClick={() => { setState({ showSignalr: false }) }}
                                                size="large">
                                                <CloseIcon />
                                            </IconButton>,
                                        ]}
                                        slotProps={{
                                            content: { style: { backgroundColor: 'white', color: 'black' } }
                                        }}
                                    />
                                </div>
                            </MenuContainer>
                            <div className='footer'>© {(new Date()).getFullYear()} Logicbroker, Inc. | <a target='_blank' rel='noopener noreferrer' href='https://www.logicbroker.com'>{'Logicbroker.com'}</a></div>
                        </div> :
                            !state.disabled && !state.error && <div style={{ height: '100vh', width: '100vw', textAlign: 'center', display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                                <div>
                                    <CircularProgress {...({determinate: 'false'} as any)} size='5em' />
                                </div>
                            </div>}
                    </Fragment>
                </BrowserRouter>
            </ThemeProvider>
        );
    }
}

export default App;
